/**
 * Default form field configurations for Spark assistant types
 * Provides default values for template form fields based on assistant type and additional parameters
 */
import { CONTROLS_LOCATION_SPECIFIC_MODULE_FIELDS } from "../controlsModuleFields";
import { normalizePmsName } from "./dentalPms";

// Keys are associatedVariable values where available (stable identifiers), falling back to field title.
// The lookup in ManageSparkLocationsForm checks associatedVariable first, then title.
export interface FormFieldConfiguration {
    [associatedVariableOrTitle: string]: string;
}

export const controlsLocationSpecificConfig: FormFieldConfiguration =
    CONTROLS_LOCATION_SPECIFIC_MODULE_FIELDS.reduce((config, associatedVariable) => {
        config[associatedVariable] = '';
        return config;
    }, {} as FormFieldConfiguration);

// ============================================================================
// DENTAL SCHEDULING CONFIGURATIONS
// ============================================================================

// Default configurations for Cloud 9 PMS
export const defaultCloud9Config: FormFieldConfiguration = {
    '{PMSName}': 'Cloud 9',
    '{defaultOfficeId}': '',
    'https://dentalWebhookBaseUrl.com': 'https://dental.voicify.com/api/cloud9',
    '{MedicalIntegrationConfigId}': '',
    '{ClientName}': '',
    '{Webhook-Application-Id}': '',
    '{Webhook-Application-Secret}': '',
    '{callBackNumberOverride}': ''
};

// Default configurations for Denticon PMS
export const defaultDenticonConfig: FormFieldConfiguration = {
    '{PMSName}': 'Denticon',
    '{defaultOfficeId}': '266',
    'https://dentalWebhookBaseUrl.com': 'https://dental.voicify.com/api/denticon',
    '{MedicalIntegrationConfigId}': '22FA374D-7BA2-439F-A1AD-F4712DD882EF',
    '{ClientName}': '',
    '{Webhook-Application-Id}': '',
    '{Webhook-Application-Secret}': '',
    '{DenticonCancelCode}': '473',
    '{callBackNumberOverride}': ''
};

// Default configurations for Dentrix Ascend PMS
export const defaultDentrixAscendConfig: FormFieldConfiguration = {
    '{PMSName}': 'Dentrix Ascend',
    '{defaultOfficeId}': '7000000000392',
    'https://dentalWebhookBaseUrl.com': 'https://dental.voicify.com/api/dentrixascend',
    '{MedicalIntegrationConfigId}': '87AC3583-8BFC-439D-9F0B-C7A450CF5E2A',
    '{ClientName}': '',
    '{Webhook-Application-Id}': '',
    '{Webhook-Application-Secret}': '',
    '{callBackNumberOverride}': ''
};

// ============================================================================
// AI ANSWERING CONFIGURATIONS
// ============================================================================

export const defaultAIAnsweringConfig: FormFieldConfiguration = {
    // Add AI Answering specific fields here
};

// ============================================================================
// AI ORDERING CONFIGURATIONS
// ============================================================================

// Sandbox Location IDs per POS — used when Environment is set to 'Voicify Sandbox' (out-of-the-box)
// OLO and ADORA have no Voicify sandbox location — user must supply the location ID.
export const SANDBOX_LOCATION_IDS: Record<string, string> = {
    'SPEEDLINE': '10138',
    'TOAST': 'af8e3bf4-a16b-4cca-b7a7-bb1be0a16021',
    'PAYTRONIX': '6716858da59fcf87d60ad76b',
};

// Common location-specific fields (independent of POS)
const commonLocationSpecificConfig: FormFieldConfiguration = {
    ...controlsLocationSpecificConfig,
    '{LocationName}': '',
    '{Restaurant-Name}': '',
    '{Assistant-Name}': 'Linda',
    '{Webhook-Restaurant-Name}': '',
    '{Webhook-Restaurant-PhoneNumber}': '',
    '{Webhook-Is-Test-Order}': '',
    '{MONDAY}': '',
    '{TUESDAY}': '',
    '{WEDNESDAY}': '',
    '{THURSDAY}': '',
    '{FRIDAY}': '',
    '{SATURDAY}': '',
    '{SUNDAY}': '',
    'DISABLED_DAYS': '',
    '{TIME_ZONE}': '',
    '{Webhook-Time-Zone}': '',
    '{Item-Welcome-Message-New-User-Response}': '',
    '{Item-Welcome-Message-Repeat-User-Response-1}': '',
    '{Item-Welcome-Message-Repeat-User-Response-2}': '',
    '{Item-Welcome-Message-Repeat-User-Response-3}': '',
    '{Item-Greeting-Message-Spanish}': '',
    '{CLOSED_WELCOME_MESSAGE}': '',
    '{Item-Welcome-Message-Closed-For-Day-Response}': '',
    '{Webhook-Use-Generative-Welcome-Message}': '',
    '{Item-Welcome-Message-Generative-Response}': '',
    '{{session.returntostorenumber}}': '',
    '{transfer-with-calling-number}': '',
    '{Webhook-Restaurant-Id}': '',
    '{Webhook-SearchAddress-Latitude}': '',
    '{Webhook-SearchAddress-Longitude}': '',
    '{LOCATION_SPECIFIC_OTHER_QUESTION_INFO}': '',
    '{UPSELL_ITEM_1}': '',
    '{UPSELL_ITEM_2}': '',
    '{UPSELL_ITEM_3}': '',
    '{UPSELL_PROMPT}': '',
    'UPSELL_VAR': 'false',
};

export const defaultAIOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
};

// Default configurations per POS name for AI Ordering

// Blank scaffold for OLO — location-specific fields left empty for user/scraper to fill
export const defaultOloOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{webhook-address-string-override}': '',
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    '{Use-Webhook-Hours}': 'false',
};

export const defaultSpeedlineOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    'https://Webhook-Speedline-Twilio-Set-Message-Url.com': '',
    'https://Webhook-Speedline-Delivery-Update-Min-Charge-Url.com': '',
    '{Use-Webhook-Hours}': 'false',
};

// Blank scaffold for Adora — location-specific fields left empty for user/scraper to fill
export const defaultAdoraOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{webhook-address-string-override}': '',
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    '{Use-Webhook-Hours}': 'false',
};

// Blank scaffold for Toast — location-specific fields left empty for user/scraper to fill
export const defaultToastOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{webhook-address-string-override}': '',
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    '{Use-Webhook-Hours}': 'true',
};

// Blank scaffold for Par POS — location-specific fields left empty for user/scraper to fill
export const defaultParOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{webhook-address-string-override}': '',
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    '{Use-Webhook-Hours}': 'true',
};

// Blank scaffold for Deliver Plus POS — location-specific fields left empty for user/scraper to fill
export const defaultDeliverPlusOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{webhook-address-string-override}': '',
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    '{Use-Webhook-Hours}': 'true',
};

// Blank scaffold for Paytronix — location-specific fields left empty for user/scraper to fill
export const defaultPaytronixOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{webhook-address-string-override}': '',
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    '{Use-Webhook-Hours}': 'false',
};

// Blank scaffold for Square — location-specific fields left empty for user/scraper to fill
export const defaultSquareOrderingConfig: FormFieldConfiguration = {
    ...commonLocationSpecificConfig,
    '{webhook-address-string-override}': '',
    '{Webhook-Allow-Delivery-With-Card}': '',
    '{Webhook-Allow-Delivery-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Cash}': '',
    '{Webhook-Allow-Pickup-With-Card}': '',
    '{Webhook-Application-Secret}': '',
    '{supportsTip}': '',
    '{Webhook-Allow-Pickup}': '',
    '{Webhook-Allow-Delivery}': '',
    '{Webhook-Ask-For-Room-Number}': '',
    '{Use-Webhook-Hours}': 'false',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get default form field configuration for Dental Scheduling based on PMS type
 * @param pmsName - The PMS type ('cloud-9', 'denticon', 'dentrix-ascend', 'open-dental')
 * @param locationName - Optional location name to populate Office Name field
 * @param appId - Optional Admin Voicify Application ID for Call Recap Email
 * @param appSecret - Optional Admin Application Secret For Call Recap Email
 * @param officeId - Optional office ID entered for this location
 * @returns FormFieldConfiguration object with default values
 */
export const getDefaultDentalSchedulingConfig = (
    pmsName: string,
    locationName?: string,
    appId?: string,
    appSecret?: string,
    officeId?: string
): FormFieldConfiguration => {
    let baseConfig: FormFieldConfiguration;

    switch (normalizePmsName(pmsName || '')) {
        case 'cloud-9':
            baseConfig = { ...defaultCloud9Config };
            break;
        case 'denticon':
            baseConfig = { ...defaultDenticonConfig };
            break;
        case 'dentrix-ascend':
            baseConfig = { ...defaultDentrixAscendConfig };
            break;
        case 'open-dental':
            baseConfig = { '{PMSName}': 'OpenDental' };
            break;
        default:
            baseConfig = {};
    }

    // Dynamically populate fields if provided
    if (locationName !== undefined) {
        baseConfig['{ClientName}'] = locationName;
        baseConfig['{LocationName}'] = locationName;
    }
    if (appId !== undefined) {
        baseConfig['{Webhook-Application-Id}'] = appId;
    }
    if (appSecret !== undefined) {
        baseConfig['{Webhook-Application-Secret}'] = appSecret;
    }
    if (officeId !== undefined) {
        baseConfig['{defaultOfficeId}'] = officeId;
    }

    return baseConfig;
};

/**
 * Get default form field configuration for AI Answering
 * @param locationName - Optional location name for location-specific fields
 * @param appId - Optional Admin Voicify Application ID
 * @param appSecret - Optional Admin Application Secret
 * @returns FormFieldConfiguration object with default values
 */
export const getDefaultAIAnsweringConfig = (
    locationName?: string,
    appId?: string,
    appSecret?: string
): FormFieldConfiguration => {
    const baseConfig = { ...defaultAIAnsweringConfig };

    if (locationName !== undefined) {
        baseConfig['{LocationName}'] = locationName;
    }
    if (appId !== undefined) {
        baseConfig['{Webhook-Application-Id}'] = appId;
    }
    if (appSecret !== undefined) {
        baseConfig['{Webhook-Application-Secret}'] = appSecret;
    }

    return baseConfig;
};

/**
 * Get default form field configuration for AI Ordering based on POS type
 * @param posName - The POS system ('OLO', 'SPEEDLINE', 'ADORA', 'TOAST', 'PAYTRONIX', 'SQUARE', 'PAR', 'DELIVERPLUS')
 * @param locationName - Optional location name for location-specific fields
 * @param appId - Optional Admin Voicify Application ID
 * @param appSecret - Optional Admin Application Secret
 * @param storeId - Optional Store ID / POS Location ID to populate {Webhook-Restaurant-Id}
 * @returns FormFieldConfiguration object with default values
 */
export const getDefaultAIOrderingConfigByPosName = (
    posName: string,
    locationName?: string,
    appId?: string,
    appSecret?: string,
    storeId?: string
): FormFieldConfiguration => {
    let baseConfig: FormFieldConfiguration;

    switch (posName) {
        case 'OLO':
            baseConfig = { ...defaultOloOrderingConfig };
            break;
        case 'SPEEDLINE':
            baseConfig = { ...defaultSpeedlineOrderingConfig };
            if (locationName !== undefined) {
                baseConfig['{Webhook-Restaurant-Name}'] = locationName;
            }
            break;
        case 'ADORA':
            baseConfig = { ...defaultAdoraOrderingConfig };
            break;
        case 'TOAST':
            baseConfig = { ...defaultToastOrderingConfig };
            break;
        case 'PAYTRONIX':
            baseConfig = { ...defaultPaytronixOrderingConfig };
            break;
        case 'SQUARE':
            baseConfig = { ...defaultSquareOrderingConfig };
            break;
        case 'PAR':
            baseConfig = { ...defaultParOrderingConfig };
            break;
        case 'DELIVERPLUS':
            baseConfig = { ...defaultDeliverPlusOrderingConfig };
            break;
        default:
            baseConfig = { ...defaultAIOrderingConfig };
    }

    // Populate {Webhook-Pos-Name} with the selected POS name
    if (posName !== undefined) {
        baseConfig['{Webhook-Pos-Name}'] = posName;
    }

    // Populate {Restaurant-Name} and {LocationName} with the location app name
    if (locationName !== undefined) {
        baseConfig['{Restaurant-Name}'] = locationName;
        baseConfig['{LocationName}'] = locationName;
    }

    // Populate {Webhook-Restaurant-Id} with the Store ID / POS Location ID
    if (storeId !== undefined) {
        baseConfig['{Webhook-Restaurant-Id}'] = storeId;
    }

    if (appId !== undefined) {
        baseConfig['{Webhook-Application-Id}'] = appId;
    }
    if (appSecret !== undefined) {
        baseConfig['{Webhook-Application-Secret}'] = appSecret;
    }

    return baseConfig;
};

/**
 * Get default form field configuration for AI Ordering
 * @param locationName - Optional location name for location-specific fields
 * @param appId - Optional Admin Voicify Application ID
 * @param appSecret - Optional Admin Application Secret
 * @param storeId - Optional Store ID / POS Location ID to populate {Webhook-Restaurant-Id}
 * @returns FormFieldConfiguration object with default values
 */
export const getDefaultAIOrderingConfig = (
    locationName?: string,
    appId?: string,
    appSecret?: string,
    storeId?: string
): FormFieldConfiguration => {
    const baseConfig = { ...defaultAIOrderingConfig };

    // Populate {Restaurant-Name} and {LocationName} with the location app name
    if (locationName !== undefined) {
        baseConfig['{Restaurant-Name}'] = locationName;
        baseConfig['{LocationName}'] = locationName;
    }

    // Populate {Webhook-Restaurant-Id} with the Store ID / POS Location ID
    if (storeId !== undefined) {
        baseConfig['{Webhook-Restaurant-Id}'] = storeId;
    }

    if (appId !== undefined) {
        baseConfig['{Webhook-Application-Id}'] = appId;
    }
    if (appSecret !== undefined) {
        baseConfig['{Webhook-Application-Secret}'] = appSecret;
    }

    return baseConfig;
};

/**
 * Universal function to get configuration for any assistant type
 * @param assistantType - The assistant type ('dental-scheduling', 'ai-answering', 'ai-ordering')
 * @param posOrPmsName - POS name for AI Ordering (e.g. 'OLO', 'SPEEDLINE', 'ADORA', 'TOAST', 'PAYTRONIX', 'SQUARE', 'PAR', 'DELIVERPLUS')
 *                       or PMS name for dental scheduling (e.g. 'cloud-9', 'denticon', 'dentrix-ascend')
 * @param locationName - Location name for dynamic fields
 * @param appId - Admin Voicify Application ID
 * @param appSecret - Admin Application Secret
 * @param locationId - Optional location ID entered in the Configuration tab. Populates
 *                     {defaultOfficeId} for Dental Scheduling or {Webhook-Restaurant-Id}
 *                     for AI Ordering.
 * @returns FormFieldConfiguration object with default values
 */
export const getDefaultAssistantConfig = (
    assistantType: string,
    posOrPmsName?: string,
    locationName?: string,
    appId?: string,
    appSecret?: string,
    locationId?: string
): FormFieldConfiguration => {
    switch (assistantType) {
        case 'dental-scheduling':
            return getDefaultDentalSchedulingConfig(posOrPmsName, locationName, appId, appSecret, locationId);

        case 'ai-answering':
            return getDefaultAIAnsweringConfig(locationName, appId, appSecret);

        case 'ai-ordering':
            return posOrPmsName
                ? getDefaultAIOrderingConfigByPosName(posOrPmsName, locationName, appId, appSecret, locationId)
                : getDefaultAIOrderingConfig(locationName, appId, appSecret, locationId);

        default:
            return {};
    }
};
